// "The Quiet Advantage", written in Krishna's own voice, from his real stories.
// Warm, humble, first-person. Short pages. His truth, not a template.

export function getNarrationText(page) {
  if (!page) return "";
  switch (page.type) {
    case "cover":
      // Written to be SPOKEN, not read. Longer flowing sentences so the model
      // has room to settle into the cloned voice instead of sounding clipped.
      return "This is a little book I wrote for founders like you. I called it The Quiet Advantage. It is honest, and I wrote it from the heart. I am Krishna, founder of Get Virtual Growth. Please take four quiet minutes with me, and read it slowly, one page at a time.";
    case "chapter":
      return `${page.title} ${(page.body || []).join(" ")}`;
    case "gratitude":
      return `${page.title} ${(page.body || []).join(" ")}`;
    case "expect":
      return `${page.title}. ${page.subtitle}. ${(page.steps || [])
        .map((s) => `${s.t}. ${s.d}`)
        .join(" ")}`;
    case "value":
      return `${page.title}. ${page.subtitle}. ${(page.items || [])
        .map((it) => `${it.name}. ${it.why}`)
        .join(" ")}`;
    case "maths":
      return `${page.title}. ${page.subtitle}. ${(page.rows || [])
        .map((r) => `${r.item}${r.note ? ", " + r.note : ""}.`)
        .join(" ")} ${page.punch || ""}`;
    case "close":
      return `${page.title}. ${(page.body || []).join(" ")} ${page.signoff || ""} ${page.author}.`;
    case "form":
      return ""; // skip narration on the lead form
    default:
      return page.title || "";
  }
}

export const BOOK = [
  {
    id: "cover",
    type: "cover",
    title: "The Quiet Advantage",
    subtitle: "A little book I wrote for founders like you. Honestly, and from the heart.",
    author: "Krishna S",
    role: "Founder, Get Virtual Growth",
    hint: "Open the book",
  },
  {
    id: "welcome",
    type: "chapter",
    num: "",
    illo: "candle",
    title: "Give me one page. That's all.",
    body: [
      "I won't ask you for much. Only this single page. If it doesn't feel worth your time, close it, and I'll have taken nothing from you but a moment.",
      "But if you're anything like the founders I sit with, you're tired in a way sleep doesn't quite fix. You carry the sales, the team, the enquiries, the reviews, the quiet 2 a.m. worry, while the very reason you started all this slips somewhere underneath it.",
      "I know that weight. I've carried it myself, and I once watched a business I loved buckle beneath it. So stay with me a few more pages. I think you'll be glad you did.",
    ],
    hint: "Let me start by telling you why I do this…",
  },
  {
    id: "origin",
    type: "chapter",
    num: "One",
    illo: "road",
    title: "Why I built this.",
    body: [
      "Get Virtual Growth wasn't born from ambition. It was born from watching a business I loved fall apart.",
      "My elder brother was one of the most respected businessmen in our city. As a boy I watched him rise. And then, slowly, I watched him lose almost everything.",
      "It wasn't one bad night. It was years of small leaks. Ignoring what was changing. Trusting the wrong people, while quieter competitors learned, adapted, and walked away with his customers.",
      "One night I asked my mother what matters more, the journey or the destination. She smiled and said, 'the companion.' That is the day I decided who I wanted to be.",
    ],
    hint: "Someone who walks beside founders, never above them…",
  },
  {
    id: "craft",
    type: "chapter",
    num: "Two",
    illo: "anyBusiness",
    title: "You know your craft. That was never the problem.",
    body: [
      "A dentist should be fixing teeth, not chasing reviews. A school should be teaching, not generating leads. A gym owner should be building your strength, not repairing a map listing. A builder should be building better homes, not filming reels.",
      "You already know your work better than anyone. What most founders don't know is what their customer is quietly looking for today, and that small gap is exactly where good businesses get left behind.",
      "So I say it simply: you do your business. We'll take care of growth and technology.",
    ],
    hint: "But first, let me show you the thing quietly costing you…",
  },
  {
    id: "leak",
    type: "chapter",
    num: "Three",
    illo: "bucket",
    title: "The leak you cannot see.",
    body: [
      "Most businesses don't lose money in one dramatic moment. They lose it in a slow, silent drip, over years.",
      "A missed call. An enquiry no one followed up. A review never asked for. A location no one can find. Effort pouring in at the top, quietly escaping at the bottom.",
      "You don't need to work harder. You need someone to find the leak, and seal it.",
    ],
    hint: "Let me show you what that looked like for others…",
  },
  {
    id: "story-developer",
    type: "chapter",
    num: "Four",
    illo: "growth",
    title: "The developer who thought he was unlucky.",
    body: [
      "I once diagnosed a real-estate developer with a beautiful location and honest pricing, yet weaker projects nearby were outselling him.",
      "It wasn't luck. Nearly 90% of his leads were slipping away, because no one truly owned them. Quietly, without blaming anyone, we built a system to catch every lead and give him a clear view of his whole pipeline.",
      "Within a few months, the project was sold out.",
    ],
    hint: "Then there was a doctor I'll never forget…",
  },
  {
    id: "story-doctor",
    type: "chapter",
    num: "Five",
    illo: "doctor",
    title: "\u201CI admire your vision, Krishna.\u201D",
    body: [
      "A brilliant eye surgeon, full of care, always busy serving at other hospitals. She had invested so much into her own clinic, yet it sat almost empty.",
      "The diagnosis was gentle but clear. She had never once told the world about her gift. Almost no online presence at all. I asked for a single month to show her the difference.",
      "Today that clinic has grown into a hospital. One day she smiled and said, 'I admire your vision, Krishna.' I still carry that.",
    ],
    hint: "And a story that almost ended in silence…",
  },
  {
    id: "story-academy",
    type: "chapter",
    num: "Six",
    illo: "map",
    title: "Seven days from closing.",
    body: [
      "A football academy was on the edge of shutting down, struggling to pay its coaches on time, despite wonderful grounds and real talent.",
      "The bottleneck? Hardly anyone in the city knew they existed. They weren't even on the map when a parent searched for directions. In seven days, that changed.",
      "The founder hugged me and laughed, 'You should be a striker on my team.' I'm not boasting. I'm humbly admitting they had everything. They simply needed to be found.",
    ],
    hint: "Now let me be honest about what I cannot do…",
  },
  {
    id: "no-magic",
    type: "chapter",
    num: "Seven",
    illo: "plate",
    title: "I don't do magic.",
    body: [
      "Let me be honest. I can't magically multiply your sales or your revenue. Anyone who promises that isn't being truthful with you.",
      "What I can do is fix your leakage, and free your team to spend more of their day truly serving your customers.",
      "I can bring people to your table and keep it reserved. But your chef still has to cook a meal worth returning for. Your customer's happiness will always be your duty. That part is sacred, and it stays yours.",
    ],
    hint: "So who is actually doing all this work?…",
  },
  {
    id: "expert-hands",
    type: "chapter",
    num: "Eight",
    illo: "globe",
    title: "You're in careful, capable hands.",
    body: [
      "I'm not a lone consultant with a laptop. Get Virtual Growth is a globally structured company, with operations in Dubai, United Arab Emirates, and a corporate entity in the United States, backed by a team that quietly improves things for our partners every single day.",
      "That structure exists for one reason: so you always feel safe. Your contracts, your compliance and your payments are handled securely, wherever in the world you are.",
      "I travel the world to sit with founders like you, while my team keeps watch over the details, so nothing you need is ever left to chance.",
      "You bring twenty years of your craft. We bring twenty years of ours.",
    ],
    hint: "Here is what actually wins customers today…",
  },
  {
    id: "value",
    type: "value",
    title: "What quietly wins customers now.",
    subtitle: "Not a menu of services. Just the few things that decide who a customer chooses.",
    items: [
      { n: "01", name: "Being found", why: "When someone searches, you appear on the map and on Google, right where the decision is made." },
      { n: "02", name: "Being trusted", why: "Reviews and a clean presence tell a stranger you're safe to choose, before you ever speak." },
      { n: "03", name: "A home online", why: "A website that answers the real questions and turns a curious visitor into a booked customer." },
      { n: "04", name: "Never losing a lead", why: "Every enquiry captured and followed up, never left to slip quietly through the cracks." },
      { n: "05", name: "Freeing your people", why: "Automation that carries the busywork, so your team can serve instead of chase." },
    ],
    hint: "And now, the part everyone worries about. The cost…",
  },
  {
    id: "maths",
    type: "maths",
    title: "The weight you're carrying.",
    subtitle: "Everything a founder is quietly expected to hire for, and manage, alone.",
    rows: [
      { item: "A receptionist", note: "to answer, book and follow up" },
      { item: "A marketing person", note: "for presence, content and reviews" },
      { item: "An agency", note: "for campaigns and the website" },
      { item: "The tools", note: "stacking up, month after month" },
    ],
    punch: "I never ask you to let anyone go. I make the people you already have far more productive, so one partner quietly does the work of a whole department.",
    hint: "Which brings me to my promise…",
  },
  {
    id: "pricing",
    type: "chapter",
    num: "Nine",
    illo: "partner",
    title: "A doctor, not a bill.",
    body: [
      "Our pricing isn't here to swallow your budget. It's here to reduce your cost and lift your productivity.",
      "We only ever recommend what you truly need. You choose the services. You can pause or cancel any time. No lock-in, ever.",
      "I win only when you win. That keeps me honest, exactly how it should be.",
    ],
    hint: "You've read this far, so now it's my turn…",
  },
  {
    id: "gratitude",
    type: "gratitude",
    title: "You gave me your time.",
    body: [
      "You're a busy founder, and you spent these quiet minutes with me. I don't take that lightly. I'm genuinely grateful.",
      "So let me give my time back to you. I'd like to personally diagnose your business, and show you where your next stage of growth is hiding.",
    ],
    hint: "Turn the page and tell me about your world",
  },
  {
    id: "form",
    type: "form",
    title: "Let me diagnose your business.",
    subtitle: "A few honest details. That's all I need to begin.",
    hint: "",
  },
  {
    id: "expect",
    type: "expect",
    title: "What happens next.",
    subtitle: "In the next 24–48 hours",
    steps: [
      { k: "01", t: "A personal diagnosis", d: "Not an auto-generated report. I will personally study your business and record a short, honest video for you." },
      { k: "02", t: "Free recommendations", d: "Quick wins that cost you nothing, small changes that lift your presence and win back lost enquiries." },
      { k: "03", t: "A foolproof growth plan", d: "A clear, step-by-step path forward, so you always know the next right move." },
    ],
    hint: "One last word before you go…",
  },
  {
    id: "close",
    type: "close",
    title: "Your success is our real win.",
    body: [
      "The happiness and success of your business, and the time it gives back to your family, is how I measure mine.",
      "You focus on what you love. Let me be the companion who walks beside you, and quietly takes care of the rest.",
    ],
    signoff: "See you soon,",
    author: "Krishna S",
    role: "Founder, Get Virtual Growth",
    hint: "",
  },
];
